import React, { useState, useEffect } from 'react';
import { X, Upload, Calendar, User, Building, MapPin, DollarSign, Users, Clock, FileText, AlertCircle, Globe, Phone, Mail, Briefcase, GraduationCap } from 'lucide-react';
import toast from 'react-hot-toast';
import DragDropUploader from './shared/DragDropUploader';

const EnhancedCandidateCreateModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    // Basic Information
    first_name: '',
    last_name: '',
    preferred_name: '',
    email: '',
    phone: '',
    mobile_phone: '',
    alternate_email: '',
    
    // Address Information
    location: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'United States',
    
    // Work Authorization
    work_authorization_status: 'authorized',
    visa_status: '',
    eligible_to_work_us: true,
    requires_sponsorship: false,
    
    // Professional Information
    current_title: '',
    current_company: '',
    current_salary: '',
    salary_expectations: {
      min: '',
      max: '',
      currency: 'USD'
    },
    notice_period_days: '',
    availability_date: '',
    
    // Experience and Skills
    total_experience_years: 0,
    relevant_experience_years: '',
    skills: [],
    technical_skills: [],
    work_experience: [],
    education: [],
    certifications: [],
    
    // Career Preferences
    desired_job_titles: [],
    preferred_locations: [],
    job_search_status: 'active',
    willing_to_relocate: false,
    remote_work_preference: 'flexible',
    
    // Profile Information
    summary: '',
    professional_summary: '',
    career_objectives: '',
    
    // Social Links
    linkedin_url: '',
    portfolio_url: '',
    github_url: '',
    
    // Recruiting Information
    source: 'manual',
    source_url: '',
    referrer_name: '',
    
    // Engagement Type (Required)
    engagement_type: 'w2',
    vendor_id: '',
    
    // Priority and Tags
    candidate_priority: 'medium',
    tags: [],
    
    // File Attachments
    resume_file_url: '',
    cover_letter_file_url: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('basic');
  const [vendors, setVendors] = useState([]);
  const [showUploader, setShowUploader] = useState(false);

  // Fetch vendors when component mounts
  useEffect(() => {
    if (isOpen) {
      fetchVendors();
    }
  }, [isOpen]);

  const fetchVendors = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/companies`);
      if (response.ok) {
        const data = await response.json();
        setVendors(data.companies || []);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation with tab mapping
    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.engagement_type) newErrors.engagement_type = 'Engagement type is required';
    if (!formData.location) newErrors.location = 'Location is required';
    
    // Vendor validation for contingent_vendor
    if (formData.engagement_type === 'contingent_vendor' && !formData.vendor_id) {
      newErrors.vendor_id = 'Vendor is required for contingent vendor engagement';
    }

    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation (basic)
    if (formData.phone && !/^\+?[\d\s\-\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Experience validation
    if (formData.total_experience_years < 0) {
      newErrors.total_experience_years = 'Experience cannot be negative';
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  // Helper function to determine which tab contains the error
  const getTabForError = (errorKey) => {
    const tabMapping = {
      // Basic Info tab
      first_name: 'basic',
      last_name: 'basic',
      preferred_name: 'basic',
      email: 'basic',
      phone: 'basic',
      mobile_phone: 'basic',
      work_authorization_status: 'basic',
      engagement_type: 'basic',
      vendor_id: 'basic',
      
      // Contact & Address tab
      location: 'contact',
      address_line1: 'contact',
      address_line2: 'contact',
      city: 'contact',
      state: 'contact',
      postal_code: 'contact',
      country: 'contact',
      alternate_email: 'contact',
      
      // Professional tab
      current_title: 'professional',
      current_company: 'professional',
      current_salary: 'professional',
      salary_expectations: 'professional',
      notice_period_days: 'professional',
      availability_date: 'professional',
      
      // Experience & Skills tab
      total_experience_years: 'experience',
      relevant_experience_years: 'experience',
      skills: 'experience',
      technical_skills: 'experience',
      work_experience: 'experience',
      education: 'experience',
      certifications: 'experience',
      
      // Preferences tab
      desired_job_titles: 'preferences',
      preferred_locations: 'preferences',
      job_search_status: 'preferences',
      willing_to_relocate: 'preferences',
      remote_work_preference: 'preferences',
      
      // Additional Info tab
      summary: 'additional',
      professional_summary: 'additional',
      career_objectives: 'additional',
      linkedin_url: 'additional',
      portfolio_url: 'additional',
      github_url: 'additional',
      source: 'additional',
      candidate_priority: 'additional',
      tags: 'additional'
    };
    
    return tabMapping[errorKey] || 'basic';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateForm();
    
    if (!validation.isValid) {
      // Get the first error field and its tab
      const errorPriority = ['first_name', 'last_name', 'email', 'engagement_type', 'vendor_id', 'location'];
      let firstErrorKey = null;
      
      for (const key of errorPriority) {
        if (validation.errors[key]) {
          firstErrorKey = key;
          break;
        }
      }
      
      if (!firstErrorKey) {
        firstErrorKey = Object.keys(validation.errors)[0];
      }
      
      if (firstErrorKey) {
        const targetTab = getTabForError(firstErrorKey);
        
        // Switch tab immediately
        setActiveTab(targetTab);
        
        // Show toast message after short delay
        setTimeout(() => {
          const errorFieldNames = {
            first_name: 'First Name',
            last_name: 'Last Name',
            email: 'Email',
            location: 'Location',
            engagement_type: 'Engagement Type',
            vendor_id: 'Vendor'
          };
          
          const fieldName = errorFieldNames[firstErrorKey] || 'Required field';
          toast.error(`Please fill in: ${fieldName}`);
        }, 200);
      }
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        skills: formData.skills,
        technical_skills: formData.technical_skills,
        desired_job_titles: formData.desired_job_titles,
        preferred_locations: formData.preferred_locations,
        tags: formData.tags,
        total_experience_years: parseInt(formData.total_experience_years) || 0,
        relevant_experience_years: formData.relevant_experience_years ? parseInt(formData.relevant_experience_years) : null,
        current_salary: formData.current_salary ? parseFloat(formData.current_salary) : null,
        notice_period_days: formData.notice_period_days ? parseInt(formData.notice_period_days) : null
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/candidates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        const result = await response.json();
        onSuccess && onSuccess(result);
        onClose();
        
        // Reset form
        resetForm();
      } else {
        const errorData = await response.json();
        alert(`Error creating candidate: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating candidate:', error);
      alert('Error creating candidate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '', last_name: '', preferred_name: '', email: '', phone: '',
      mobile_phone: '', alternate_email: '', location: '', address_line1: '',
      address_line2: '', city: '', state: '', postal_code: '', country: 'United States',
      work_authorization_status: 'authorized', visa_status: '', eligible_to_work_us: true,
      requires_sponsorship: false, current_title: '', current_company: '',
      current_salary: '', salary_expectations: { min: '', max: '', currency: 'USD' },
      notice_period_days: '', availability_date: '', total_experience_years: 0,
      relevant_experience_years: '', skills: [], technical_skills: [], work_experience: [],
      education: [], certifications: [], desired_job_titles: [], preferred_locations: [],
      job_search_status: 'active', willing_to_relocate: false, remote_work_preference: 'flexible',
      summary: '', professional_summary: '', career_objectives: '', linkedin_url: '',
      portfolio_url: '', github_url: '', source: 'manual', source_url: '',
      referrer_name: '', candidate_priority: 'medium', tags: [], resume_file_url: '',
      cover_letter_file_url: ''
    });
    setErrors({});
  };

  const handleArrayFieldChange = (field, value) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData({ ...formData, [field]: items });
  };

  const handleResumeParsed = (parsedData) => {
    // Map parsed resume data to form fields
    setFormData(prev => ({
      ...prev,
      first_name: parsedData.first_name || prev.first_name,
      last_name: parsedData.last_name || prev.last_name,
      email: parsedData.email || prev.email,
      phone: parsedData.phone || prev.phone,
      location: parsedData.location || prev.location,
      current_title: parsedData.current_position || prev.current_title,
      current_company: parsedData.current_company || prev.current_company,
      total_experience_years: parsedData.experience_years || prev.total_experience_years,
      skills: parsedData.skills || prev.skills,
      technical_skills: parsedData.skills || prev.technical_skills,
      summary: parsedData.summary || prev.summary,
      professional_summary: parsedData.summary || prev.professional_summary,
      certifications: parsedData.certifications || prev.certifications,
      work_experience: parsedData.previous_positions || prev.work_experience
    }));

    // Close uploader and show success message
    setShowUploader(false);
    toast.success('Resume parsed successfully! Please review and complete remaining fields.');
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: User },
    { id: 'contact', label: 'Contact & Address', icon: Phone },
    { id: 'professional', label: 'Professional', icon: Briefcase },
    { id: 'experience', label: 'Experience & Skills', icon: GraduationCap },
    { id: 'preferences', label: 'Preferences', icon: MapPin },
    { id: 'additional', label: 'Additional Info', icon: FileText }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.first_name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="John"
                />
                {errors.first_name && <span className="text-xs text-red-500">{errors.first_name}</span>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.last_name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Smith"
                />
                {errors.last_name && <span className="text-xs text-red-500">{errors.last_name}</span>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Name</label>
              <input
                type="text"
                value={formData.preferred_name}
                onChange={(e) => setFormData({ ...formData, preferred_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Johnny (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="john.smith@example.com"
              />
              {errors.email && <span className="text-xs text-red-500">{errors.email}</span>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.phone ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="+1 (555) 123-4567"
                />
                {errors.phone && <span className="text-xs text-red-500">{errors.phone}</span>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Phone</label>
                <input
                  type="tel"
                  value={formData.mobile_phone}
                  onChange={(e) => setFormData({ ...formData, mobile_phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+1 (555) 987-6543"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Work Authorization Status</label>
              <select
                value={formData.work_authorization_status}
                onChange={(e) => setFormData({ ...formData, work_authorization_status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="authorized">Authorized to work</option>
                <option value="needs_sponsorship">Needs sponsorship</option>
                <option value="student_visa">Student visa</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.eligible_to_work_us}
                  onChange={(e) => setFormData({ ...formData, eligible_to_work_us: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">Eligible to work in US</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.requires_sponsorship}
                  onChange={(e) => setFormData({ ...formData, requires_sponsorship: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">Requires sponsorship</span>
              </label>
            </div>

            {/* Engagement Type Section */}
            <div className="pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Engagement Type</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Engagement Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.engagement_type}
                  onChange={(e) => setFormData({ ...formData, engagement_type: e.target.value, vendor_id: e.target.value !== 'contingent_vendor' ? '' : formData.vendor_id })}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.engagement_type ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="w2">W2 - Direct Employee</option>
                  <option value="contingent_1099">Contingent 1099 - Independent Contractor</option>
                  <option value="contingent_vendor">Contingent Vendor - Through Staffing Agency</option>
                </select>
                {errors.engagement_type && <span className="text-xs text-red-500">{errors.engagement_type}</span>}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.engagement_type === 'w2' && 'Direct hire as W-2 employee'}
                  {formData.engagement_type === 'contingent_1099' && 'Independent contractor with 1099 tax status'}
                  {formData.engagement_type === 'contingent_vendor' && 'Candidate provided by staffing agency or vendor'}
                </p>
              </div>

              {/* Vendor Selection - Conditional */}
              {formData.engagement_type === 'contingent_vendor' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vendor/Staffing Agency <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.vendor_id}
                    onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.vendor_id ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a vendor...</option>
                    {vendors.map(vendor => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.company_name || vendor.vendor_name}
                      </option>
                    ))}
                  </select>
                  {errors.vendor_id && <span className="text-xs text-red-500">{errors.vendor_id}</span>}
                  {vendors.length === 0 && (
                    <p className="mt-1 text-xs text-amber-600">
                      No vendors found. Please add vendors first from the Vendor Management page.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Location *</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.location ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Dallas, TX"
              />
              {errors.location && <span className="text-xs text-red-500">{errors.location}</span>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
              <input
                type="text"
                value={formData.address_line1}
                onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="123 Main Street"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
              <input
                type="text"
                value={formData.address_line2}
                onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Apartment, suite, etc."
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Dallas"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="TX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="75201"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <select
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Australia">Australia</option>
                <option value="Germany">Germany</option>
                <option value="France">France</option>
                <option value="India">India</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Email</label>
              <input
                type="email"
                value={formData.alternate_email}
                onChange={(e) => setFormData({ ...formData, alternate_email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="alternate@example.com"
              />
            </div>
          </div>
        );

      case 'professional':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Job Title</label>
                <input
                  type="text"
                  value={formData.current_title}
                  onChange={(e) => setFormData({ ...formData, current_title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Senior Software Engineer"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Company</label>
                <input
                  type="text"
                  value={formData.current_company}
                  onChange={(e) => setFormData({ ...formData, current_company: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tech Corp Inc."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Salary ($)</label>
                <input
                  type="number"
                  value={formData.current_salary}
                  onChange={(e) => setFormData({ ...formData, current_salary: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="120000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notice Period (Days)</label>
                <input
                  type="number"
                  value={formData.notice_period_days}
                  onChange={(e) => setFormData({ ...formData, notice_period_days: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="14"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salary Expectations</label>
              <div className="grid grid-cols-3 gap-3">
                <input
                  type="number"
                  value={formData.salary_expectations.min}
                  onChange={(e) => setFormData({
                    ...formData,
                    salary_expectations: { ...formData.salary_expectations, min: e.target.value }
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Min"
                />
                <input
                  type="number"
                  value={formData.salary_expectations.max}
                  onChange={(e) => setFormData({
                    ...formData,
                    salary_expectations: { ...formData.salary_expectations, max: e.target.value }
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Max"
                />
                <select
                  value={formData.salary_expectations.currency}
                  onChange={(e) => setFormData({
                    ...formData,
                    salary_expectations: { ...formData.salary_expectations, currency: e.target.value }
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Availability Date</label>
              <input
                type="date"
                value={formData.availability_date}
                onChange={(e) => setFormData({ ...formData, availability_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Search Status</label>
              <select
                value={formData.job_search_status}
                onChange={(e) => setFormData({ ...formData, job_search_status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Actively looking</option>
                <option value="passive">Passively looking</option>
                <option value="not_looking">Not looking</option>
                <option value="employed">Currently employed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Professional Summary</label>
              <textarea
                value={formData.professional_summary}
                onChange={(e) => setFormData({ ...formData, professional_summary: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief professional summary..."
              />
            </div>
          </div>
        );

      case 'experience':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Experience (Years)</label>
                <input
                  type="number"
                  value={formData.total_experience_years}
                  onChange={(e) => setFormData({ ...formData, total_experience_years: parseInt(e.target.value) || 0 })}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.total_experience_years ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                />
                {errors.total_experience_years && <span className="text-xs text-red-500">{errors.total_experience_years}</span>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relevant Experience (Years)</label>
                <input
                  type="number"
                  value={formData.relevant_experience_years}
                  onChange={(e) => setFormData({ ...formData, relevant_experience_years: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Skills <span className="text-xs text-gray-500">(Comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.skills.join(', ')}
                onChange={(e) => handleArrayFieldChange('skills', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="JavaScript, React, Node.js, Python"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Technical Skills <span className="text-xs text-gray-500">(Comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.technical_skills.join(', ')}
                onChange={(e) => handleArrayFieldChange('technical_skills', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="AWS, Docker, Kubernetes, MySQL"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Certifications <span className="text-xs text-gray-500">(Comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.certifications.join(', ')}
                onChange={(e) => handleArrayFieldChange('certifications', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="AWS Certified, PMP, Scrum Master"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Desired Job Titles <span className="text-xs text-gray-500">(Comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.desired_job_titles.join(', ')}
                onChange={(e) => handleArrayFieldChange('desired_job_titles', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Software Engineer, Full Stack Developer, Technical Lead"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Career Objectives</label>
              <textarea
                value={formData.career_objectives}
                onChange={(e) => setFormData({ ...formData, career_objectives: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Career goals and aspirations..."
              />
            </div>
          </div>
        );

      case 'preferences':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preferred Locations <span className="text-xs text-gray-500">(Comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.preferred_locations.join(', ')}
                onChange={(e) => handleArrayFieldChange('preferred_locations', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Dallas TX, Austin TX, Remote"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remote Work Preference</label>
              <select
                value={formData.remote_work_preference}
                onChange={(e) => setFormData({ ...formData, remote_work_preference: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="remote_only">Remote Only</option>
                <option value="hybrid">Hybrid</option>
                <option value="onsite">Onsite Only</option>
                <option value="flexible">Flexible</option>
              </select>
            </div>

            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.willing_to_relocate}
                  onChange={(e) => setFormData({ ...formData, willing_to_relocate: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">Willing to relocate</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Candidate Priority</label>
              <select
                value={formData.candidate_priority}
                onChange={(e) => setFormData({ ...formData, candidate_priority: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="hot">Hot</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags <span className="text-xs text-gray-500">(Comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.tags.join(', ')}
                onChange={(e) => handleArrayFieldChange('tags', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="senior, react-expert, startup-experience"
              />
            </div>
          </div>
        );

      case 'additional':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
              <input
                type="url"
                value={formData.linkedin_url}
                onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://linkedin.com/in/username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Portfolio URL</label>
              <input
                type="url"
                value={formData.portfolio_url}
                onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://portfolio.example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">GitHub URL</label>
              <input
                type="url"
                value={formData.github_url}
                onChange={(e) => setFormData({ ...formData, github_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://github.com/username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
              <select
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="manual">Manual Entry</option>
                <option value="linkedin">LinkedIn</option>
                <option value="indeed">Indeed</option>
                <option value="monster">Monster</option>
                <option value="referral">Referral</option>
                <option value="career_site">Career Site</option>
                <option value="agency">Agency</option>
              </select>
            </div>

            {formData.source === 'referral' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Referrer Name</label>
                <input
                  type="text"
                  value={formData.referrer_name}
                  onChange={(e) => setFormData({ ...formData, referrer_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Name of person who referred this candidate"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Resume File URL</label>
              <input
                type="url"
                value={formData.resume_file_url}
                onChange={(e) => setFormData({ ...formData, resume_file_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://example.com/resume.pdf"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cover Letter File URL</label>
              <input
                type="url"
                value={formData.cover_letter_file_url}
                onChange={(e) => setFormData({ ...formData, cover_letter_file_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://example.com/cover-letter.pdf"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">General Summary</label>
              <textarea
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="General candidate summary and notes..."
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold text-gray-900">Add New Candidate</h2>
              <button
                type="button"
                onClick={() => setShowUploader(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
              >
                <Upload className="w-4 h-4" />
                Upload Resume
              </button>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-6 mt-4 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="h-full flex flex-col">
            <div className="flex-1 p-6">
              {renderTabContent()}
            </div>
            
            {/* Footer - Inside Form */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  * Required fields
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? 'Adding...' : 'Add Candidate'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Drag and Drop Uploader Modal */}
      {showUploader && (
        <DragDropUploader
          uploadType="resume"
          onFileParsed={handleResumeParsed}
          onClose={() => setShowUploader(false)}
        />
      )}
    </div>
  );
};

export default EnhancedCandidateCreateModal;